import React, { useEffect, useState, useMemo } from "react"; // Import useMemo
import {
  Grid,
  Card,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  CircularProgress,
  Typography,
  CardMedia,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import ImageIcon from "@mui/icons-material/Image";
import api from "core/interceptors/api";
import Swal from "sweetalert2";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { ServerBaseUrlForStaticImages } from "constants";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import LogoutButton from "layouts/logout";
import { useNavigate } from "react-router-dom";

// Modify the columns definition to also accept page and rowsPerPage for S.No calculation
const columns = (handleView, handleEdit, handleDelete, page, rowsPerPage) => [
  {
    Header: "S.No",
    accessor: "serialNumber", // New accessor for serial number
    Cell: ({ row, flatRows }) => (page * rowsPerPage) + flatRows.indexOf(row) + 1, // Calculate serial number
    disableSortBy: true, // Optional: disable sorting for serial number
  },
  { Header: "Business Name", accessor: "business_name" },
  { Header: "Phone", accessor: "phone_number" },
  { Header: "Created By", accessor: "created_by" },
  {
    Header: "Actions",
    accessor: "actions",
    Cell: ({ row }) => (
      <>
        <IconButton color="info" onClick={() => handleView(row.original)}>
          <VisibilityIcon />
        </IconButton>
        <IconButton color="success" onClick={() => handleEdit(row.original)}>
          <EditIcon />
        </IconButton>
        <IconButton color="error" onClick={() => handleDelete(row.original.id)}>
          <DeleteIcon />
        </IconButton>
      </>
    ),
  },
];

function BusinessAdsPage() {
  const [ads, setAds] = useState([]); // Stores all fetched ads
  const [filteredAds, setFilteredAds] = useState([]); // Stores ads after applying search filter
  const [searchQuery, setSearchQuery] = useState(""); // State for global search input

  const [openForm, setOpenForm] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [formData, setFormData] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [viewData, setViewData] = useState(null);
  const [isUpdate, setIsUpdate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formLoading, setFormLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [formErrors, setFormErrors] = useState({});
  const navigate = useNavigate();

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchAds();
  }, []);

  // Effect for global search filtering
  useEffect(() => {
    const lowerCaseQuery = searchQuery.toLowerCase();
    const newFilteredAds = ads.filter(ad => {
      const businessNameMatch = ad.business_name.toLowerCase().includes(lowerCaseQuery);
      const phoneNumberMatch = ad.phone_number.toLowerCase().includes(lowerCaseQuery);
      const createdByMatch = ad.created_by.toLowerCase().includes(lowerCaseQuery);
      return businessNameMatch || phoneNumberMatch || createdByMatch;
    });
    setFilteredAds(newFilteredAds);
    setPage(0); // Reset to the first page of filtered results
  }, [searchQuery, ads]); // Depend on searchQuery and the full ads list

  const fetchAds = async () => {
    setLoading(true);
    try {
      const res = await api.get("/business_ads/");
      if (res.data.status === 1) {
        setAds(res.data.ads); // Set all fetched ads
        // No need to set filteredAds here, the useEffect will handle it
        // based on the initial empty searchQuery
      } else {
        setSnackbar({ open: true, message: res.data.msg || "Failed to fetch ads.", severity: "error" });
      }
    } catch (error) {
      console.error("Error fetching ads:", error);
      setSnackbar({ open: true, message: "Error fetching ads from server.", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setFormData({});
    setImageFile(null);
    setImagePreviewUrl(null);
    setIsUpdate(false);
    setFormErrors({});
  };

  const handleCloseView = () => {
    setOpenView(false);
    setViewData(null);
  };

  const handleOpenForm = () => {
    const email = localStorage.getItem("email") || "";
    setFormData({ business_name: "", phone_number: "", description: "", created_by: email });
    setImageFile(null);
    setImagePreviewUrl(null);
    setIsUpdate(false);
    setFormErrors({});
    setOpenForm(true);
  };

  const handleEdit = (data) => {
    setFormData(data);
    setIsUpdate(true);
    setFormErrors({});
    if (data.ad_image) {
      setImagePreviewUrl(`${ServerBaseUrlForStaticImages}/business_ads/uploads/${data.ad_image}`);
    } else {
      setImagePreviewUrl(null);
    }
    setOpenForm(true);
  };

  const handleView = (data) => {
    setViewData(data);
    setOpenView(true);
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This will delete the ad permanently.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
    });
    if (confirm.isConfirmed) {
      try {
        setLoading(true);
        const res = await api.delete(`/business_ads/${id}`);
        if (res.data.status === 1) {
          Swal.fire("Deleted!", "Ad has been deleted.", "success");
          fetchAds(); // Re-fetch to update the table
        } else {
          Swal.fire("Error", res.data.msg || "Failed to delete ad.", "error");
        }
      } catch (error) {
        console.error("Error deleting ad:", error);
        Swal.fire("Error", "An error occurred while deleting the ad.", "error");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];

    // Clear previous image preview immediately
    setImageFile(null);
    setImagePreviewUrl(null);

    if (!file) return;

    // 1. Check file size (max 2MB)
    const maxSizeInBytes = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSizeInBytes) {
      setSnackbar({ open: true, message: "Image size should be less than 2MB.", severity: "error" });
      return;
    }

    // 2. Check image dimensions
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      const requiredWidth = 1200;
      const requiredHeight = 400;

      if (img.width !== requiredWidth || img.height !== requiredHeight) {
        setSnackbar({
          open: true,
          message: `Image dimensions should be ${requiredWidth}x${requiredHeight}px.`,
          severity: "error",
        });
        URL.revokeObjectURL(objectUrl);
        return;
      }

      // Valid file: set preview and file
      setImageFile(file);
      setImagePreviewUrl(objectUrl);
      setFormErrors(prev => ({ ...prev, ad_image: '' }));
    };

    img.onerror = () => {
      setSnackbar({ open: true, message: "Failed to load image.", severity: "error" });
      URL.revokeObjectURL(objectUrl);
    };

    img.src = objectUrl;
  };



  const handleClearImage = () => {
    setImageFile(null);
    setImagePreviewUrl(null);
    setFormData(prev => ({ ...prev, ad_image: null })); // Explicitly set to null to indicate removal
    setFormErrors(prev => ({ ...prev, ad_image: '' }));
  };

  const validateField = (name, value) => {
    let error = '';
    switch (name) {
      case 'business_name':
        if (!value || value.trim().length < 2) {
          error = "Business Name is required and must be at least 2 characters.";
        }
        break;
      case 'phone_number':
        const phoneRegex = /^[0-9]{10}$/;
        if (!value || !phoneRegex.test(value)) {
          error = "Phone Number is required and must be a 10-digit number.";
        }
        break;
      case 'description':
        if (!value || value.trim().length < 10) {
          error = "Description is required and must be at least 10 characters.";
        }
        break;
      default:
        break;
    }
    return error;
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    const error = validateField(name, value);
    setFormErrors(prev => ({ ...prev, [name]: error }));
  };

  const validateFormOnSubmit = () => {
    let errors = {};
    let isValid = true;

    const fieldsToValidate = ['business_name', 'phone_number', 'description'];
    fieldsToValidate.forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        errors[field] = error;
        isValid = false;
      }
    });

    setFormErrors(errors);
    return isValid;
  };

  const handleFormSubmit = async () => {
    if (!validateFormOnSubmit()) {
      setSnackbar({ open: true, message: "Please correct the form errors.", severity: "error" });
      return;
    }

    setFormLoading(true);
    try {
      const email = localStorage.getItem("email") || "";
      const dataToSubmit = {
        ...formData,
        created_by: email,
      };

      const form = new FormData();
      for (const key in dataToSubmit) {
        // Handle explicit null for ad_image when clearing it
        if (key === "ad_image" && dataToSubmit[key] === null) {
          form.append(key, ""); // Send an empty string or specific indicator for null
        } else if (key !== "ad_image") { // Exclude ad_image to handle it separately
          form.append(key, dataToSubmit[key]);
        }
      }

      if (imageFile) {
        form.append("ad_image", imageFile);
      } else if (isUpdate && formData.ad_image === null) {
        // If it's an update and ad_image was explicitly cleared, send a flag
        // This flag should be understood by your backend to clear the existing image.
        // Make sure your backend API for updating handles this "ad_image_cleared" flag.
        form.append("ad_image_cleared", "true");
      }


      const method = isUpdate ? api.put : api.post;
      const url = isUpdate ? `/business_ads/${formData.id}` : "/business_ads/";

      const res = await method(url, form);
      if (res.data.status === 1) {
        Swal.fire("Success", res.data.msg, "success");
        handleCloseForm();
        fetchAds(); // Re-fetch to update the table with new data
      } else {
        Swal.fire("Error", res.data.msg || "Operation failed.", "error");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      Swal.fire("Error", "An unexpected error occurred. Please try again.", "error");
    } finally {
      setFormLoading(false);
    }
  };

  // Pagination handlers
  const handleRowsPerPageChange = (event, newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page when rows per page changes
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  // Memoized data for the current page, based on filtered ads
  const paginatedTableData = useMemo(() => {
    return filteredAds.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredAds, page, rowsPerPage]);

  return (
    <DashboardLayout>
      <MDBox display="flex" justifyContent="flex-end" alignItems="center">
        <LogoutButton
          onLogout={() => {
            navigate("/login");
          }}
        />
      </MDBox>
      <DashboardNavbar />
      <MDBox pt={4} pb={3}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card>

              <MDBox display="flex" justifyContent="space-between" alignItems="center" p={2}>
                <MDTypography variant="h6">Business Advertisements</MDTypography>

                {/* Search input for global filtering */}

                <MDButton onClick={handleOpenForm} variant="gradient" color="success">
                  Add Business Ad
                </MDButton>
              </MDBox>
              <MDBox pl={3} pt={2} pb={2}>
                <TextField
                  label="Search ads..."
                  variant="outlined"
                  size="small"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{ width: '300px' }}
                />
              </MDBox>
              <MDBox p={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <CircularProgress />
                  </MDBox>
                ) : (
                  <DataTable
                    table={{
                      columns: columns(handleView, handleEdit, handleDelete, page, rowsPerPage), // Pass page and rowsPerPage
                      rows: paginatedTableData, // Use paginated data
                    }}
                    canSearch={false} // Disable internal DataTable search, as we handle it globally
                    noEndBorder
                    pagination={{
                      variant: "contained",
                      color: "dark",
                      page: page,
                      onPageChange: handlePageChange,
                      rowsPerPage: rowsPerPage,
                      onRowsPerPageChange: handleRowsPerPageChange,
                      count: filteredAds.length, // Total count of items for pagination (of filtered data)
                    }}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Form Dialog (Add/Update Ad) */}
      <Dialog open={openForm} onClose={handleCloseForm} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'info.main', color: 'white' }}>
          <MDTypography variant="subtitle2" color="white">{isUpdate ? "Edit" : "Create"} Business Ad</MDTypography>
          <IconButton
            aria-label="close"
            onClick={handleCloseForm}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Business Name"
            fullWidth
            margin="normal"
            required
            name="business_name"
            value={formData.business_name || ""}
            onChange={handleFieldChange}
            error={!!formErrors.business_name}
            helperText={formErrors.business_name}
          />
          <TextField
            label="Phone Number"
            fullWidth
            margin="normal"
            required
            name="phone_number"
            value={formData.phone_number || ""}
            onChange={handleFieldChange}
            error={!!formErrors.phone_number}
            helperText={formErrors.phone_number}
          />
          <TextField
            label="Description"
            fullWidth
            margin="normal"
            multiline
            rows={2}
            required
            name="description"
            value={formData.description || ""}
            onChange={handleFieldChange}
            error={!!formErrors.description}
            helperText={formErrors.description}
          />
          <MDBox mt={2}>
            <input
              type="file"
              accept="image/*"
              id="ad-image-upload"
              style={{ display: "none" }}
              onChange={handleImageChange}
            />
            <label htmlFor="ad-image-upload">
              <MDButton component="span" variant="outlined" color="info">
                Upload Ad Image
              </MDButton>
            </label>
            {/* Show required dimensions info */}
            <Typography variant="caption" color="textSecondary" display="block" mt={1}>
              Recommended: 1200x400 px, max size 2MB
            </Typography>

            {imagePreviewUrl && (
              <MDBox mt={2} display="flex" alignItems="center" sx={{ position: 'relative', width: 'fit-content' }}>
                <img
                  src={imagePreviewUrl}
                  alt="Ad Preview"
                  style={{ maxWidth: "200px", maxHeight: "150px", borderRadius: 8, objectFit: 'contain', border: '1px solid #ddd' }}
                />
                <IconButton
                  onClick={handleClearImage}
                  sx={{
                    position: 'absolute',
                    top: -10,
                    right: -10,
                    backgroundColor: 'rgba(255,255,255,0.8)',
                    '&:hover': { backgroundColor: 'white' },
                    padding: '4px',
                  }}
                  size="small"
                >
                  <CloseIcon sx={{ fontSize: '1.2rem', color: 'red' }} />
                </IconButton>
              </MDBox>
            )}
            {formErrors.ad_image && (
              <Typography variant="caption" color="error" display="block" mt={1}>
                {formErrors.ad_image}
              </Typography>
            )}
            {/* Display message for existing image if no new image selected during update */}
            {!imagePreviewUrl && (formData.ad_image && isUpdate && formData.ad_image !== null) ? (
              <MDBox mt={2} display="flex" alignItems="center" gap={1} color="text.secondary">
                <ImageIcon fontSize="small" />
                <Typography variant="caption">Existing image will be kept unless a new one is uploaded.</Typography>
              </MDBox>
            ) : (!imagePreviewUrl && !imageFile && !isUpdate) ? ( // No image selected at all for a new ad
              <MDBox mt={2} display="flex" alignItems="center" gap={1} color="text.secondary">
                <ImageIcon fontSize="small" />
                <Typography variant="caption">No image selected</Typography>
              </MDBox>
            ) : null}
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: 'background.paper', borderTop: '1px solid #eee' }}>
          <Button onClick={handleCloseForm} disabled={formLoading}>Cancel</Button>
          <MDButton onClick={handleFormSubmit} variant="contained" color="success" disabled={formLoading}>
            {formLoading ? <CircularProgress size={24} color="inherit" /> : (isUpdate ? "Save Changes" : "Create Ad")}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={openView} onClose={handleCloseView} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'info.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <MDTypography variant="subtitle2" color="white">Ad Details</MDTypography>
          <IconButton
            aria-label="close"
            onClick={handleCloseView}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: 'background.default', p: 3 }}>
          {viewData && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card elevation={3} sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
                  <MDTypography variant="subtitle1" color="text.primary" mb={1}>
                    <strong>Business Name:</strong> {viewData.business_name}
                  </MDTypography>
                  <MDTypography variant="body2" color="text.secondary">
                    <strong>Phone:</strong> {viewData.phone_number}
                  </MDTypography>
                  <MDTypography variant="body2" color="text.secondary">
                    <strong>Created By:</strong> {viewData.created_by}
                  </MDTypography>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card elevation={3} sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
                  <MDTypography variant="subtitle1" color="text.primary">
                    <strong>Description:</strong>
                  </MDTypography>
                  <MDTypography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                    {viewData.description || "No description provided."}
                  </MDTypography>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card elevation={3} sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <MDTypography variant="subtitle1" color="text.primary" mb={2}>
                    <strong>Ad Image:</strong>
                  </MDTypography>
                  {viewData.ad_image ? (
                    <Box sx={{ border: '1px solid #ddd', borderRadius: 2, overflow: 'hidden', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#f5f5f5', width: '100%', maxWidth: 400, height: 250 }}>
                      <CardMedia
                        component="img"
                        image={`${ServerBaseUrlForStaticImages}/business_ads/uploads/${viewData.ad_image}`}
                        alt="Business Ad"
                        sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: 'contain' }}
                      />
                    </Box>
                  ) : (
                    <MDBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="150px" sx={{ border: '1px dashed #ccc', borderRadius: 2, mt: 2, backgroundColor: '#f0f0f0', width: '100%', maxWidth: 400 }}>
                      <ImageIcon color="disabled" sx={{ fontSize: 48 }} />
                      <MDTypography variant="body2" color="text.secondary" mt={1}>No Image Available</MDTypography>
                    </MDBox>
                  )}
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: 'background.paper', borderTop: '1px solid #eee' }}>
          <Button onClick={handleCloseView} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MuiAlert elevation={6} variant="filled" onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default BusinessAdsPage;